import { useCallback, useEffect, useMemo, useState } from 'react'

const STORAGE_KEY = 'focus-room.timer.v1'

interface PersistedTimer {
  durationSeconds: number
  remainingSeconds: number
  endsAt: number | null
}

function loadTimer(): PersistedTimer {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '') as PersistedTimer
    if (!Number.isFinite(parsed.durationSeconds)) throw new Error('invalid')
    return parsed
  } catch {
    return { durationSeconds: 25 * 60, remainingSeconds: 25 * 60, endsAt: null }
  }
}

export function formatTime(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds))
  const minutes = Math.floor(safe / 60)
  const seconds = safe % 60
  return `${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}`
}

const SOUND_KEY = 'focus-room.timer.sound'

export function useTimer(onComplete?: () => void) {
  const initial = useMemo(loadTimer, [])
  const [durationSeconds, setDurationSeconds] = useState(initial.durationSeconds)
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem(SOUND_KEY) !== 'off')
  const [remainingSeconds, setRemainingSeconds] = useState(() => {
    if (!initial.endsAt) return initial.remainingSeconds
    return Math.max(0, Math.ceil((initial.endsAt - Date.now()) / 1000))
  })
  const [endsAt, setEndsAt] = useState<number | null>(initial.endsAt)
  const running = endsAt !== null

  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ durationSeconds, remainingSeconds, endsAt }),
    )
  }, [durationSeconds, endsAt, remainingSeconds])

  useEffect(() => {
    localStorage.setItem(SOUND_KEY, soundEnabled ? 'on' : 'off')
  }, [soundEnabled])

  useEffect(() => {
    if (!endsAt) return
    const tick = () => {
      const next = Math.max(0, Math.ceil((endsAt - Date.now()) / 1000))
      setRemainingSeconds(next)
      if (next === 0) {
        setEndsAt(null)
        if (soundEnabled) onComplete?.()
      }
    }
    tick()
    const interval = window.setInterval(tick, 250)
    return () => window.clearInterval(interval)
  }, [endsAt, onComplete, soundEnabled])

  const toggle = useCallback(() => {
    if (endsAt) {
      setRemainingSeconds(Math.max(0, Math.ceil((endsAt - Date.now()) / 1000)))
      setEndsAt(null)
      return
    }
    const startFrom = remainingSeconds > 0 ? remainingSeconds : durationSeconds
    setRemainingSeconds(startFrom)
    setEndsAt(Date.now() + Math.max(startFrom, 1) * 1000)
  }, [durationSeconds, endsAt, remainingSeconds])

  const setMinutes = useCallback((minutes: number) => {
    const seconds = Math.max(60, Math.min(240 * 60, Math.round(minutes * 60)))
    setDurationSeconds(seconds)
    setRemainingSeconds(seconds)
    setEndsAt(null)
  }, [])

  /** Sets the duration and starts (or restarts) the countdown in one step. */
  const startWith = useCallback((minutes: number) => {
    const seconds = Math.max(60, Math.min(240 * 60, Math.round(minutes * 60)))
    setDurationSeconds(seconds)
    setRemainingSeconds(seconds)
    setEndsAt(Date.now() + seconds * 1000)
  }, [])

  const adjust = useCallback(
    (minutes: number) => {
      setMinutes(Math.max(1, Math.round(durationSeconds / 60) + minutes))
    },
    [durationSeconds, setMinutes],
  )

  const reset = useCallback(() => {
    setEndsAt(null)
    setRemainingSeconds(durationSeconds)
  }, [durationSeconds])

  return {
    display: formatTime(remainingSeconds),
    remainingSeconds,
    durationMinutes: Math.round(durationSeconds / 60),
    running,
    progress: durationSeconds > 0 ? 1 - remainingSeconds / durationSeconds : 0,
    soundEnabled,
    setSoundEnabled,
    toggle,
    setMinutes,
    startWith,
    adjust,
    reset,
  }
}
